import { ApiError, toApiError } from './problem'
import { apiBaseUrl } from './client'

/**
 * Server-sent events over `fetch`.
 *
 * `EventSource` cannot be used for the assistant: the API streams from
 * `POST /assistant/messages/stream` because the prompt is the body and prompts do
 * not belong in a URL. `EventSource` only issues GETs and cannot set an
 * Authorization header, so the body stream is read by hand.
 *
 * The same reader serves `GET /jobs/{id}/events`.
 */
export interface SseEvent {
  /** The `event:` field. Defaults to `message` when the server omits it. */
  event: string
  /** The concatenated `data:` payload, newline-joined per the SSE spec. */
  data: string
}

/** Parses an SSE byte stream into events. */
export async function* readSseStream(
  body: ReadableStream<Uint8Array>,
  signal?: AbortSignal,
): AsyncGenerator<SseEvent> {
  const reader = body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  try {
    while (true) {
      if (signal?.aborted === true) return

      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })

      // Events are separated by a blank line. Normalize CRLF first so a server
      // or proxy using \r\n framing does not silently produce zero events.
      buffer = buffer.replace(/\r\n/g, '\n')

      let separator = buffer.indexOf('\n\n')
      while (separator !== -1) {
        const raw = buffer.slice(0, separator)
        buffer = buffer.slice(separator + 2)

        const parsed = parseEvent(raw)
        if (parsed !== null) yield parsed

        separator = buffer.indexOf('\n\n')
      }
    }

    // A final event with no trailing blank line still counts.
    const tail = parseEvent(buffer)
    if (tail !== null) yield tail
  } finally {
    reader.releaseLock()
  }
}

function parseEvent(raw: string): SseEvent | null {
  if (raw.trim().length === 0) return null

  let event = 'message'
  const data: string[] = []

  for (const line of raw.split('\n')) {
    if (line.startsWith(':')) continue // comment / keep-alive
    const colon = line.indexOf(':')
    const field = colon === -1 ? line : line.slice(0, colon)
    // A single leading space after the colon is part of the framing, not the data.
    const value = colon === -1 ? '' : line.slice(colon + 1).replace(/^ /, '')

    if (field === 'event') event = value
    else if (field === 'data') data.push(value)
  }

  if (data.length === 0 && event === 'message') return null
  return { event, data: data.join('\n') }
}

export interface SseRequestOptions {
  method?: 'GET' | 'POST'
  body?: unknown
  accessToken?: string | null | undefined
  signal?: AbortSignal | undefined
}

/**
 * Opens an SSE request and yields its events.
 *
 * Errors surface as {@link ApiError} before the first event, matching `request`
 * in client.ts — so a caller handles one error type whether it streams or not.
 */
export async function* streamEvents(
  path: string,
  options: SseRequestOptions = {},
): AsyncGenerator<SseEvent> {
  const headers: Record<string, string> = { Accept: 'text/event-stream' }
  if (options.body !== undefined) headers['Content-Type'] = 'application/json'
  if (options.accessToken !== null && options.accessToken !== undefined) {
    headers.Authorization = `Bearer ${options.accessToken}`
  }

  const init: RequestInit = { method: options.method ?? 'GET', headers }
  if (options.body !== undefined) init.body = JSON.stringify(options.body)
  if (options.signal !== undefined) init.signal = options.signal

  const url = `${apiBaseUrl()}${path.startsWith('/') ? path : `/${path}`}`

  let response: Response
  try {
    response = await fetch(url, init)
  } catch (cause) {
    throw new ApiError({
      kind: 'network',
      status: 0,
      message: cause instanceof Error ? cause.message : 'Stream request failed.',
    })
  }

  if (!response.ok) throw await toApiError(response)
  if (response.body === null) {
    throw new ApiError({ kind: 'unknown', status: response.status, message: 'Empty event stream.' })
  }

  yield* readSseStream(response.body, options.signal)
}

/** Parses an event's `data` as JSON. Returns null when the payload is not JSON. */
export function sseData<T>(event: SseEvent): T | null {
  try {
    return JSON.parse(event.data) as T
  } catch {
    return null
  }
}
